//User wanted to add the key to app displaying via popup on clicking "add key"

// Library Utilities
import React, { useState, useRef } from "react";

import PortalOverlay from "../../../../Shared/portal-overlay/portalOverlay";
// Components
import Input from "../../../../Shared/input/input";
import Button from "../../../../Shared/button/button";
import ErrorMessage from "../../../../Shared/error-message/errorMessage";

// Utilities
import {
  cancelButtonHoverStyle,
  cancelButtonStyle,
  primaryButtonHoverStyle,
  primaryButtonStyle,
} from "../../../../Shared/buttonStyles";

// Function
import validator from "validator";
import { callApi } from "../../../../api/fetch";
import { unwrapListData } from "../../../../utils/dashboardMappers";
import { isValidName } from "../../../../utils/validation";
import { saveTrialAppApiKey } from "../../../../utils/trialAppApiKey";

// Constants
import { MEDIUM } from "../../../../Shared/buttonSize";
import WarningIllustration from "../../../../assets/images/Warning.svg";

// Styles
import Classes from "./addApiKey.module.scss";
import Styles from "../../../../Shared/popup.module.scss";
import { Copy } from "lucide-react";
import { testIdProps } from "../../../../utils/testIdProps";

function AddAppKey({ onCancel, onSubmit, appId, apiKey = null }: any) {
  const [appKey, setAppKey] = useState("");
  const [error, setError] = useState("");
  const [validKeyName, setValidKeyName] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [createdKeys, setCreatedKeys] = useState<
    Record<string, unknown>[] | null
  >(null);
  const apiKeyRef = useRef<any>(null);
  const [copied, setCopied] = useState(false);

  const revealedApiKey =
    apiKey || (data && (data.api_key as string | undefined));

  function checkAppKey() {
    if (validator.isEmpty(appKey, { ignore_whitespace: true })) {
      setValidKeyName(false);
      setError("Key name cannot be empty");
      return false;
    }
    if (isValidName(appKey, true)) {
      setValidKeyName(false);
      setError("Key name should contain alphanumeric char only");
      return false;
    }
    if (validator.isNumeric(appKey[0], { no_symbols: true })) {
      setValidKeyName(false);
      setError("Key name should not start with a number");
      return false;
    }
    if (appKey.length > 25) {
      setValidKeyName(false);
      setError("Key name should be less than 25 character");
      return false;
    }
    return true;
  }

  function handleAppKeyCancel(event: any) {
    event.preventDefault();
    // Refresh parent key list only after the one-time reveal is dismissed.
    if (createdKeys && onSubmit) {
      onSubmit(createdKeys);
    }
    onCancel();
    return;
  }

  function handleCopyApiKey() {
    const range = new Range();

    range.setStart(apiKeyRef.current, 0);
    range.setEnd(apiKeyRef.current, 1);

    // apply the selection
    window.document.getSelection()?.removeAllRanges();
    window.document.getSelection()?.addRange(range);

    window.document.execCommand("copy");

    setCopied(true);
  }

  async function handleAppKey(event: any) {
    event.preventDefault();

    if (loading || !checkAppKey()) return;

    try {
      setLoading(true);
      const createdKeyRaw = await callApi(`/apps/${appId}/create-key`, {
        method: "POST",
        data: { key_name: appKey },
      });
      const createdKey = createdKeyRaw as Record<string, unknown>;
      const keysData = await callApi(
        `/keys?app_ids=${encodeURIComponent(appId)}`,
        { method: "GET" }
      );
      const keys = unwrapListData<Record<string, unknown>>(keysData, "keys");
      setLoading(false);

      // Keep modal mounted so the one-time api_key can be shown; refresh parent on Close.
      setCreatedKeys(keys);
      setData(createdKey);
      // this is the one-time key that is displayed in the modal
      const oneTimeKey = String(createdKey.api_key ?? "").trim();
      if (oneTimeKey) {
        saveTrialAppApiKey(appId, oneTimeKey);
      }
    } catch (err) {
      setLoading(false);
      setError((err as Error).message);
    }
  }

  return (
    <PortalOverlay>
      <div
      className={`d-flex align-items-center justify-content-center ${Styles.overlay}`}
    >
      {revealedApiKey ? (
        <div
          className={`p-4 ${Styles.popUpContent}`}
          style={{ width: "500px" }}
        >
          <div className="py-2 text-center">
            <img
              src={WarningIllustration}
              alt="warning-illustration"
              style={{ width: "100px" }}
            />
          </div>
          <div className="py-2">
            <p className={`mb-0 ${Classes.headingText}`}>API Key</p>
          </div>
          <div className="py-2 d-flex gap-2 align-content-center justify-content-center position-relative">
            <p className={`mb-0 ${Classes.key}`} ref={apiKeyRef}>
              {revealedApiKey}
            </p>
            <div style={{ position: "relative", fontSize: "12px" }}>
              <span
                {...testIdProps("manage-apps-copy-api-key")}
                style={{ display: "inline-flex", cursor: "pointer" }}
              >
                <Copy
                  style={{ color: "#0f9d58" }}
                  onClick={handleCopyApiKey}
                />
              </span>
              {copied ? (
                <span
                  style={{
                    position: "absolute",
                    top: "-30px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#222",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                  }}
                >
                  Copied!
                </span>
              ) : (
                <span
                  style={{
                    position: "absolute",
                    top: "-30px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#222",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                  }}
                >
                  Copy
                </span>
              )}
            </div>
          </div>
          <div className="py-3 px-4">
            <p className={`mb-0 ${Classes.subtext}`}>
              Please copy the generated API key to save the same. <br /> You
              will not be able to retrieve this later.
            </p>
          </div>
          <div className="py-3 text-center" style={{ margin: "0 auto" }}>
            <div className="py-3 d-flex justify-content-center">
              <p
                {...testIdProps("manage-apps-api-key-close")}
                className={`mb-0 d-inline-flex ${Classes.closeText}`}
                onClick={handleAppKeyCancel}
              >
                Close
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className={`p-4 ${Styles.popUpContent}`}>
          <form onSubmit={handleAppKey}>
            <div className="form-group">
              <p className="title_semibold_text mb-3">App Key</p>

              <Input
                type="text"
                name="appKey"
                id="manage-apps-add-api-key-name"
                autoComplete="off"
                maxLength={25}
                isValid={validKeyName}
                placeholder="Name your key"
                hasError={error}
                // labelname="App Key"
                onChange={(event: any) => {
                  const value = event.target.value;
                  setAppKey(value.trim());
                  setError("");
                  setValidKeyName(true);
                }}
                onBlur={checkAppKey}
                customInputClass="border-radius-10"
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </div>
            <div className="d-flex align-items-center justify-content-between py-2 gap-3">
              <div className="pr-2 w-100">
                <Button
                  id="manage-apps-add-api-key-cancel"
                  size={MEDIUM}
                  onClick={handleAppKeyCancel}
                  hoveredStyle={cancelButtonHoverStyle}
                  style={cancelButtonStyle}
                  type="button"
                  className="w-100"
                >
                  Cancel
                </Button>
              </div>
              <div className="w-100">
                <Button
                  id="manage-apps-add-api-key-submit"
                  size={MEDIUM}
                  isLoading={loading}
                  hoveredStyle={primaryButtonHoverStyle}
                  style={primaryButtonStyle}
                  type="submit"
                  className="w-100"
                >
                  Add key
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div></PortalOverlay>
  );
}

export default AddAppKey;
